import React, { useEffect, useState, createContext, useContext } from "react" 
import { useParams, useSearchParams } from "react-router-dom"
import { appTools, cn, queryTools } from "lib/utils"
import { useAppCtx, appStore } from "lib/state"
import { makeNewConditional } from "lib/utils/conditionals"
import { useDynamicLayout } from "lib/hooks"
import { CollectionLoader } from "lib/components"
import { ConditionalWorkingArea, ConditionalTestingArea, Welcome } from "lib/units/conditional"
import { useDispute } from "lib/units/dispute/hooks"
import { useStatements } from "lib/units/dispute/statements"

const ConditionalContext = createContext({})
export const useConditionalCtx = () => useContext(ConditionalContext)

const defaultAssistantMessage = `
  <div class="text-center">Items in the same block (dotted orange line) are OR statements</div>
  <div class="text-center">Blocks on the same row (light blue background) are AND statements</div>
  <div class="text-center">Statements on separate rows are OR statements</div>
`

const newConditional = { name: "", description: "", zones: [], items: [] }

export default function ConditionalWrapper({ 
  usage="local",
  elemtype,
  children 
}:{ 
  usage?:"local" | "global";
  elemtype?:string;
  children?:React.ReactNode 
}):JSX.Element {
  
  const { dspid } = useParams()
  const [ searchParams, setSearchParams ] = useSearchParams() 

  const { appState, appTools }:any = useAppCtx()
  const { store, AppStore }:any = appStore()
  const { update }:any = AppStore

  const { data:_Dispute } = queryTools.resource.parseQueryForApi("dispute.findById", { id: dspid }, { enabled: !!dspid })
  const saveFn = queryTools.resource.save(["dispute"])

  const { DynamicWidthLayout, toggleExpanded, expandedState } = useDynamicLayout({ init: "expanded" })
  const { updateConditionalInDispute } = useStatements({ elemtype })
  const { replaceElementConfig } = useDispute({ saveFn })

  const { data:_Conditionals } = queryTools.resource.parseQueryForApi("conditional.findAll", {}, { enabled: true })


  const [ currentConditional, setCurrentConditional ] = useState<any>(null)

  async function handleConditionalSave(e:any, data:any){
    if(e) e.preventDefault()
    console.log(data)
    saveFn.mutate({ resource: "conditional", data: {...data, _id: data._id} })
  }

  const updateConditional = (data:any) => {
    console.log("data", data)
    if( !data ) data = makeNewConditional()
    if( usage === "local" ){
      return update("actives.statement.conditional", {...store.actives.statement.conditional, ...data })
    } else {
      return update("actives.conditional", {...store.actives.conditional, ...data })
    }
  }

  const saveChanges = () => {
    if( !elemtype ) return
    const statementRefStr = searchParams.get("st")
    if( !statementRefStr ) return
    const condToSave = store.actives.statement.conditional
    const updatedStatements = updateConditionalInDispute(_Dispute, statementRefStr, condToSave)
    replaceElementConfig(_Dispute, elemtype, updatedStatements)
  }

  useEffect(() => {
    let current 
    if( usage === "local" ){
      if( !store.actives.statement.conditional ){
        const cond = makeNewConditional()
        update("actives.statement.conditional", cond)
        current = cond
      } else {
        current = store.actives.statement.conditional
      }
    } else if (usage === "global"){
      if( !store.actives.conditional ){
        const cond = makeNewConditional()
        update("actives.conditional", cond)
        current = cond
      } else {
        current = store.actives.conditional
      }
    }
    setCurrentConditional(current)
  },[store.actives.statement?.conditional, store.actives.conditional, usage])

  useEffect(() => {
    if( _Dispute && searchParams.get("st") ){
      console.log(elemtype)
      const statement = _Dispute?.elements.find( (elem:any) => elem.refstr === elemtype)?.settings.config.find( (st:any) => st.refstr === searchParams.get("st") )
      console.log("statement", statement)
      if( statement ){
        update("actives.statement", statement)
      }
    }
  },[_Dispute, searchParams.get("st")])

  return (
    <DynamicWidthLayout 
      className={cn("w-full h-full items-stretch", expandedState ? "gap-4" : "gap-2")}
      expandedWidths={[ "w-2/12", "w-10/12" ]} 
      collapsedWidths={[ "w-[2rem]", "w-[calc(100%-2rem)]" ]}
    >
      <div className="h-full overflow-y-scroll scrollbar border border-solid border-slate-700 *:text-gray-50">
        <CollectionLoader 
          title="Conditionals"
          className=""
          collection={_Conditionals || []} 
          labelProp="name" 
          showDescriptions={true}
          draggable={true}
          expanded={expandedState}
          toggleExpanded={toggleExpanded}
        />
      </div>

      <ConditionalContext.Provider value={{ usage, currentConditional, makeNewConditional, updateConditional, handleConditionalSave }}>
        <div className="w-full h-full relative">
          <div className="w-full flex flex-col grow">
            { currentConditional === undefined ? (
              <Welcome 
                className={cn(
                  "h-full flex flex-col grow border border-solid border-slate-700 relative",
                  store.location.mode === "builder" ? "w-full" : "w-9/12"
                )} 
              />
            ) : (
              <>
                <ConditionalWorkingArea 
                  className={cn(
                    "border border-solid border-slate-700 relative *:text-gray-50",
                    (store.location.mode === "builder" || currentConditional?.zones.length === 0) ? "w-[98%]" : "7/12"
                  )}
                />
                { store.location.mode !== "builder" && currentConditional?.zones.length > 0 && (
                  <ConditionalTestingArea 
                    className="w-3/12 border border-solid border-slate-700 relative" 
                  /> 
                )}
              </>
            )}
          </div>

          <div className="w-full h-8 mt-[2px]">
            <button 
              onClick={saveChanges}
              className="w-full h-full bg-teal-700 flex justify-center items-center text-xs uppercase"
            >
              save changes before closing
            </button>
          </div>
        </div>
      </ConditionalContext.Provider>
    </DynamicWidthLayout>
  )
}
